import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Loader2, XCircle, PartyPopper } from "lucide-react";
import { fetchPaymentStatus, authMe } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PaymentResult = ({ kind }) => {
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const [state, setState] = useState(kind === "cancel" ? "cancel" : "checking");
  const tries = useRef(0);

  useEffect(() => {
    if (kind !== "success") return;
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    const poll = async () => {
      try {
        const res = await fetchPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setState("paid");
          authMe().then(setUser).catch(() => {});
          return;
        }
      } catch {}
      tries.current += 1;
      if (tries.current < 12) setTimeout(poll, 2000);
      else setState("failed");
    };
    poll();
  }, [kind, params, setUser]);

  return (
    <div className="grid min-h-screen place-items-center bg-[#FFFDF6] p-4" data-testid="payment-result-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-3xl border border-[#E2DDD0] bg-white p-10 text-center shadow-[0_20px_40px_-10px_rgba(14,165,233,0.15)]"
      >
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#FFC107]" />
            <h1 className="mt-5 font-display text-2xl font-bold text-slate-900">Confirming your membership…</h1>
            <p className="mt-2 text-sm text-slate-500">One moment while Stripe tucks everything in.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 16 }}>
              <PartyPopper className="mx-auto h-12 w-12 text-[#FFC107]" />
            </motion.div>
            <h1 className="mt-5 font-display text-2xl font-bold text-slate-900" data-testid="payment-success-title">
              Welcome to FAMILY+!
            </h1>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              The whole universe is unlocked — full library, downloads, printables and bedtime
              collections. Happy singing!
            </p>
            <Link
              to="/profile"
              data-testid="payment-success-profile-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 px-8 py-3.5 font-display text-base font-semibold text-white"
            >
              <Crown className="h-4 w-4 text-[#FFC107]" /> Go to my profile
            </Link>
          </>
        )}
        {(state === "cancel" || state === "failed") && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-[#FF6B6B]" />
            <h1 className="mt-5 font-display text-2xl font-bold text-slate-900" data-testid="payment-cancel-title">
              {state === "cancel" ? "No worries — maybe later" : "We couldn't confirm that payment"}
            </h1>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              {state === "cancel"
                ? "Checkout was cancelled. Your free account is still full of joy."
                : "If you completed checkout, give it a minute and check your profile."}
            </p>
            <Link
              to="/#pricing"
              data-testid="payment-back-btn"
              className="mt-6 inline-block rounded-full bg-[#FFC107] px-8 py-3.5 font-display text-base font-semibold text-slate-900"
            >
              Back to plans
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentResult;
